<template>
  <div class="chat-thread">
    <header v-if="showHeader" class="chat-header">
      <div class="chat-header-meta">
        <span v-if="userId" class="meta-item"><strong>User:</strong> {{ userId }}</span>
        <span v-if="sessionId" class="meta-item"><strong>Session:</strong> {{ sessionId }}</span>
        <span v-if="updatedAt" class="meta-item"><strong>Updated:</strong> {{ formatDate(updatedAt) }}</span>
      </div>
    </header>
    <div ref="scrollEl" class="chat-messages">
      <div
        v-for="(msg, idx) in normalizedMessages"
        :key="idx"
        class="chat-bubble-wrap"
        :class="bubbleClass(msg.role)"
      >
        <div class="chat-bubble">
          <span class="chat-role">{{ roleLabel(msg.role) }}</span>
          <pre class="chat-content">{{ msg.content }}</pre>
        </div>
      </div>
      <p v-if="!normalizedMessages.length" class="chat-empty">Нет сообщений</p>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, nextTick, ref, watch } from 'vue';

const props = defineProps({
  messages: { type: [Array, Object, String], default: () => [] },
  userId: { type: String, default: '' },
  sessionId: { type: String, default: '' },
  updatedAt: { type: [String, Date], default: null },
  showHeader: { type: Boolean, default: true }
});

const scrollEl = ref(null);

function normalizeMessages(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((m) => {
      if (typeof m === 'string') return { role: 'unknown', content: m };
      const role = m.role || m.type || 'unknown';
      let content = m.content ?? m.text ?? m.message ?? '';
      if (typeof content !== 'string') content = JSON.stringify(content, null, 2);
      return { role, content };
    });
  }
  if (typeof raw === 'object') {
    return [{ role: 'system', content: JSON.stringify(raw, null, 2) }];
  }
  return [{ role: 'unknown', content: String(raw) }];
}

const normalizedMessages = computed(() => normalizeMessages(props.messages));

function bubbleClass(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'user' || r === 'human') return 'is-user';
  if (r === 'assistant' || r === 'ai' || r === 'bot') return 'is-assistant';
  return 'is-system';
}

function roleLabel(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'user' || r === 'human') return 'User';
  if (r === 'assistant' || r === 'ai' || r === 'bot') return 'Assistant';
  if (r === 'system') return 'System';
  return role || 'Message';
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('ru-RU');
}

async function scrollToBottom() {
  await nextTick();
  const el = scrollEl.value;
  if (el) el.scrollTop = el.scrollHeight;
}

watch(normalizedMessages, scrollToBottom, { immediate: true });
</script>

<style scoped>
.chat-thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 280px;
  background: var(--p-content-hover-background);
  border: 1px solid var(--p-content-border-color);
  border-radius: 10px;
  overflow: hidden;
}

.chat-header {
  padding: 0.75rem 1rem;
  background: var(--p-content-background);
  border-bottom: 1px solid var(--p-content-border-color);
  position: sticky;
  top: 0;
  z-index: 1;
}

.chat-header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.85rem;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.chat-bubble-wrap {
  display: flex;
  width: 100%;
}

.chat-bubble-wrap.is-user {
  justify-content: flex-end;
}

.chat-bubble-wrap.is-assistant,
.chat-bubble-wrap.is-system {
  justify-content: flex-start;
}

.chat-bubble {
  max-width: min(85%, 720px);
  padding: 0.65rem 0.85rem;
  border-radius: 12px;
  background: var(--p-content-background);
  border: 1px solid var(--p-content-border-color);
  color: var(--p-text-color);
}

.is-user .chat-bubble {
  background: color-mix(in srgb, var(--p-primary-color) 14%, var(--p-content-background));
  border-color: color-mix(in srgb, var(--p-primary-color) 35%, var(--p-content-border-color));
}

.chat-role {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  opacity: 0.65;
  margin-bottom: 0.35rem;
}

.chat-content {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.9rem;
  line-height: 1.45;
}

.chat-empty {
  text-align: center;
  color: var(--p-text-muted-color);
  padding: 2rem;
}

</style>
